/**
 * Client-side mirror of the API error envelope defined in
 * `docs/architecture/api-standards.md`:
 *
 *   { "error": { "code": "...", "message": "...", "details": [ ... ] } }
 */

export type ApiErrorDetail = {
  field: string;
  issue: string;
};

export type ApiErrorEnvelope = {
  error: {
    code: string;
    message: string;
    details?: ApiErrorDetail[];
  };
};

/** Error codes the client can produce itself (the server never sends these). */
export const CLIENT_ERROR_CODES = {
  network: 'NETWORK_ERROR',
  timeout: 'TIMEOUT',
  unknown: 'INTERNAL_ERROR',
} as const;

export class ApiError extends Error {
  /** HTTP status, or `0` when the request never reached the server. */
  readonly status: number;
  readonly code: string;
  readonly details?: ApiErrorDetail[];

  constructor(status: number, code: string, message: string, details?: ApiErrorDetail[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (details && details.length > 0) {
      this.details = details;
    }
  }

  /** Maps `details` onto react-hook-form field names. */
  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const detail of this.details ?? []) {
      if (detail.field && result[detail.field] === undefined) {
        result[detail.field] = detail.issue;
      }
    }
    return result;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

function isEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = (value as { error?: unknown }).error;
  if (typeof candidate !== 'object' || candidate === null) return false;
  const { code, message } = candidate as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}

function parseDetails(value: unknown): ApiErrorDetail[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const details = value.filter(
    (item): item is ApiErrorDetail =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as ApiErrorDetail).field === 'string' &&
      typeof (item as ApiErrorDetail).issue === 'string',
  );
  return details.length > 0 ? details : undefined;
}

/**
 * Builds an `ApiError` from a non-2xx response body. A missing or
 * unparseable body still yields a usable error — never `undefined`.
 */
export function apiErrorFromBody(status: number, body: unknown): ApiError {
  if (isEnvelope(body)) {
    return new ApiError(
      status,
      body.error.code,
      body.error.message,
      parseDetails(body.error.details),
    );
  }
  return new ApiError(status, CLIENT_ERROR_CODES.unknown, 'Unexpected server error');
}

export function networkError(
  message = 'Cannot reach the server. Check your connection.',
): ApiError {
  return new ApiError(0, CLIENT_ERROR_CODES.network, message);
}

/**
 * The request exceeded its budget. Distinct from `networkError` so the retry
 * policy can treat it differently: the caller already waited the full timeout,
 * and repeating it only multiplies that wait for the same likely outcome.
 */
export function timeoutError(message = 'The server took too long to respond.'): ApiError {
  return new ApiError(0, CLIENT_ERROR_CODES.timeout, message);
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
